import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as Pin } from '@/assets/icons/pin.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import { ReactComponent as UserIcon } from '@/assets/icons/user-plus.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import BackboneUserBadge from '@/components/BackboneUserBadge';
import { Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import InvoicesMemberModal from '@/pages/InvoiceList/components/InvoicesMemberModal';
import {
  BankMark,
  PayoneerMark,
  PaypalMark,
} from '@/pages/InvoiceList/components/PaymentMethodIcon';
import { InvoicesMemberResource } from '@/types/resources';
import { api } from '@/utils/api';

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 168px;
`;

const Badges = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 8px;
`;

const ReceiverTd = styled(Td)`
  position: relative;
`;

export const SyledPin = styled(Pin)`
  margin-right: 4px;
  margin-right: 4px;
  margin-bottom: -3px;
`;

const ReceiverWrapper = styled.div`
  display: flex;
  white-space: nowrap;
`;

const ContentHolder = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContentDot = styled.div`
  width: 4px;
  height: 4px;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 50%;
  margin-right: 6px;
  transform: translateY(1px);
`;

interface Props {
  member: InvoicesMemberResource;
}

const InvoicesPreferencesTableRow: FC<Props> = ({ member }) => {
  const [showMemberModal, setShowMemberModal] = useState(false);

  const canCreateFreelancers = useHasPermission('manage freelancers');

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this receiver?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/payments/members/${member.id}`,
      );

      queryClient.invalidateQueries(['invoices_members'], {
        exact: true,
      });

      queryClient.invalidateQueries(['invoices_list'], {
        exact: true,
      });

      alerts.success('Member was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const onFreelancerCreate = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to add this user as freelancer?',
      );

      if (!confirm) return;

      await api.post<never, { id: number }>(
        `/v1/payments/members/create-freelancer/${member.id}`,
      );

      queryClient.invalidateQueries(['freelancers_details'], {
        exact: false,
      });

      queryClient.invalidateQueries(['invoices_members'], {
        exact: false,
      });

      alerts.success('Freelancer was successfully created');
    } catch (e) {
      alerts.error();
    }
  };

  const trueReceivers = Array.from(
    new Set(member.banks.map((b) => b.true_receiver).filter((r) => !!r)),
  );

  return (
    <tr>
      <ReceiverTd>
        <ReceiverWrapper>
          {!!member.banks?.length && <BankMark />}
          {!!member.paypal_email && <PaypalMark />}
          {!!member.payoneer_email && <PayoneerMark />}
          {!!member.freelancer_id && (
            <Badges>
              <BackboneUserBadge type="freelancer" />
            </Badges>
          )}
          <Text $type="smallHeadline">{member.name}</Text>
        </ReceiverWrapper>
      </ReceiverTd>
      <Td>
        {trueReceivers.length
          ? trueReceivers.map((tr) => (
              <ContentHolder key={tr}>
                <ContentDot /> {tr}
              </ContentHolder>
            ))
          : '-'}
      </Td>
      <Td>
        {!!member.invoices_count ? (
          <NavLink to={`/general/invoices`} state={{ memberName: member.name }}>
            <SyledPin />
            {member.invoices_count}
          </NavLink>
        ) : (
          '-'
        )}
      </Td>
      <Td $actionsCol>
        <StyledActionsDropdown>
          <ActionsDropdownButton
            as="button"
            onClick={() => setShowMemberModal(true)}
          >
            <span>
              <PencilIcon />
            </span>
            Edit member
          </ActionsDropdownButton>
          {canCreateFreelancers && (
            <ActionsDropdownButton
              disabled={!!member.freelancer_id}
              as="button"
              onClick={onFreelancerCreate}
            >
              <span>
                <UserIcon />
              </span>
              Add as Freelancer
            </ActionsDropdownButton>
          )}
          <ActionsDropdownButton as="button" onClick={onDelete}>
            <span>
              <TrashIcon />
            </span>
            Delete
          </ActionsDropdownButton>
        </StyledActionsDropdown>
      </Td>
      <AnimatePresence>
        {showMemberModal && (
          <InvoicesMemberModal
            member={member}
            onClose={() => setShowMemberModal(false)}
          />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default InvoicesPreferencesTableRow;
